import { router } from '@inertiajs/react'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Plus, Pencil, Trash2, Film } from 'lucide-react'
import { useState } from 'react'

interface SessionSummary {
  id: string
  name: string
  slug: string
  status: string
  clip_url: string | null
  recorded_at: string | null
  updated_at: string
}

interface TemplateSummary {
  id: string
  name: string
  updated_at: string
}

interface ReferenceCandidate {
  id: string
  name: string
}

interface Props {
  sessions: SessionSummary[]
  templates: TemplateSummary[]
  reference_candidates: ReferenceCandidate[]
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 30) return `${diffDays} days ago`
  return date.toLocaleDateString()
}

function StatusIndicator({ status }: { status: string }) {
  const map: Record<string, { label: string; className: string; dot: string }> = {
    draft: { label: 'Draft', className: 'text-amber-600', dot: 'bg-amber-400' },
    recording: { label: 'Recording', className: 'text-blue-600', dot: 'bg-blue-500 animate-pulse' },
    recorded: { label: 'Recorded', className: 'text-emerald-600', dot: 'bg-emerald-500' },
    failed: { label: 'Failed', className: 'text-red-600', dot: 'bg-red-500' },
  }
  const s = map[status] || map.draft
  return (
    <span className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      <span className={`text-xs font-medium ${s.className}`}>{s.label}</span>
    </span>
  )
}

export default function DemosIndex({ sessions, templates, reference_candidates: referenceCandidates }: Props) {
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const [creatingTemplate, setCreatingTemplate] = useState(false)

  const handleCreateTemplate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTemplateName.trim()) return
    setCreatingTemplate(true)
    router.post('/demos/templates', {
      template: { name: newTemplateName.trim() }
    }, {
      onFinish: () => setCreatingTemplate(false),
      onSuccess: () => {
        setTemplateDialogOpen(false)
        setNewTemplateName('')
      }
    })
  }

  return (
    <Layout>
      <div className="p-8">
        {/* Demos Section */}
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Demos</h1>
            <p className="mt-1 text-sm text-gray-500">
              AI-generated 390×844 mobile animations, recorded to MP4.
            </p>
          </div>
          <Button variant="outline" onClick={() => router.post('/demos')}>
            <Plus className="mr-1 h-4 w-4" />
            New Demo
          </Button>
        </div>

        <div className="mt-6">
          {sessions.length === 0 ? (
            <p className="text-sm text-gray-500">No demos yet. Describe what you want and create your first demo.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="grid grid-cols-[110px_1fr_90px_140px_50px] border-b border-gray-200 bg-gray-50 px-5 py-3">
                <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Status</span>
                <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Name</span>
                <span className="text-center text-xs font-medium uppercase tracking-wider text-gray-400">Clip</span>
                <span className="text-right text-xs font-medium uppercase tracking-wider text-gray-400">Updated</span>
                <span />
              </div>
              {sessions.map(session => (
                <div
                  key={session.id}
                  className="grid grid-cols-[110px_1fr_90px_140px_50px] items-center border-b border-gray-200 px-5 py-3.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <StatusIndicator status={session.status} />
                  <span
                    className="cursor-pointer text-sm font-medium text-gray-900 hover:text-blue-600"
                    onClick={() => router.visit(`/demos/${session.id}`)}
                  >
                    {session.name}
                  </span>
                  <span className="text-center text-sm text-gray-400">
                    {session.clip_url ? (
                      <a
                        href={session.clip_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600"
                      >
                        <Film className="h-3 w-3" />
                        MP4
                      </a>
                    ) : '—'}
                  </span>
                  <span className="text-right text-sm text-gray-400">{formatTimeAgo(session.updated_at)}</span>
                  <div className="text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (confirm(`Delete "${session.name}"?`)) {
                          router.delete(`/demos/${session.id}`)
                        }
                      }}
                      className="p-1 text-gray-300 transition-colors hover:text-red-500"
                      title="Delete demo"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Templates Section */}
        <div className="mt-10">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Templates</h2>
              <p className="mt-1 text-xs text-gray-500">
                Reusable starting points for new demos. HabitWager paradigms are pre-seeded.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setTemplateDialogOpen(true)}>
              <Plus className="mr-1 h-3 w-3" />
              New Template
            </Button>
          </div>

          {templates.length === 0 ? (
            <p className="text-sm text-gray-500">No templates yet.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="grid grid-cols-[1fr_140px_140px] border-b border-gray-200 bg-gray-50 px-5 py-3">
                <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Name</span>
                <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Updated</span>
                <span className="text-right text-xs font-medium uppercase tracking-wider text-gray-400">Actions</span>
              </div>
              {templates.map(template => (
                <div
                  key={template.id}
                  className="grid grid-cols-[1fr_140px_140px] items-center border-b border-gray-200 px-5 py-3.5 transition-colors last:border-b-0 hover:bg-gray-50"
                >
                  <span className="text-sm font-medium text-gray-900">{template.name}</span>
                  <span className="text-sm text-gray-400">{formatTimeAgo(template.updated_at)}</span>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => router.post(`/demos/${template.id}/edit_template`)}
                    >
                      <Pencil className="mr-1 h-3 w-3" />
                      Edit
                    </Button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete template "${template.name}"?`)) {
                          router.delete(`/demos/templates/${template.id}`)
                        }
                      }}
                      className="p-1 text-gray-300 transition-colors hover:text-red-500"
                      title="Delete template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Template</DialogTitle>
              <DialogDescription>Create a blank template scaffolded with the demo animation conventions. You can then customize it with the agent.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  placeholder="e.g. Minimal Dark"
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={creatingTemplate || !newTemplateName.trim()}>
                  {creatingTemplate ? 'Creating...' : 'Create Template'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  )
}
